import { PERMISSIONS, type PermissionKey } from "@shared/permissions";
import type { MeInstance } from "@shared/types";
import { MCP_APPROVE_PATH } from "@shared/mcp/writeIntents";
import { MCP_CONSENT_PATH } from "@shared/mcp/consent";
import { splitInstancePath } from "@/lib/instancePath";
import {
  AuditIcon,
  ConnectionsIcon,
  ConnectorsIcon,
  InstancesIcon,
  OverviewIcon,
  PlatformSettingsIcon,
  RunsIcon,
  SchedulesIcon,
  SettingsIcon,
  TenantsIcon,
  AiAppsIcon,
  ConnectedAppsIcon,
  ApproveChangeIcon,
  TransformationsIcon,
  WatchIcon,
  UsersIcon,
  SkyMonitorIcon,
  YourDataIcon,
  type IconComponent,
} from "./icons";

/**
 * Every navigable route, declared ONCE (UX-1, UX-3).
 *
 * The nav drawer, the page header and the route guard all read this table, so a
 * new screen cannot be reachable by nav and unlabelled in the header, or
 * labelled and unreachable. The alternative — a switch for the title and a
 * separate list for the nav — is how a route ends up wearing another page's
 * subtitle: a missing case falls through to a real page's title, which looks
 * complete and is wrong.
 *
 * `anyPermission` is "any one of these is enough". A route with none is visible
 * to anyone signed in. Hiding is not the control (FR-13) — the server enforces
 * the same permission — but the two must agree.
 */

/**
 * The path of every screen, named once.
 *
 * A screen that links to another screen — Tenants to Instances, the egress tab
 * to Connectors — spells the destination here rather than inline, so a path
 * that moves moves in one place and a typo is a type error rather than a
 * silently dead link.
 */
export const ROUTES = {
  overview: "/overview",
  connections: "/connections",
  schedules: "/schedules",
  runs: "/runs",
  transformations: "/transformations",
  // ADR 0011 (2026-10-08): where the data lands, in the slot the Explorer had (`/explorer`
  // redirects here, `legacyAddress`).
  yourData: "/your-data",
  monitor: "/monitor",
  // B-51a (round 10): the Settings tabs share one prefix. General is `/settings`; the others are
  // tabs under it. Notifications is not in APP_ROUTES (it inherits the General guard, no nav entry,
  // contract-notif R10). Connectors (T-51) asks for its own key (`SETTINGS_TABS`). The addresses
  // they had before (`/destination`, `/admin/settings...`) redirect (`legacyAddress`).
  settings: "/settings",
  settingsDestination: "/settings/destination",
  settingsNotifications: "/settings/notifications",
  settingsConnectors: "/settings/connectors",
  adminUsers: "/admin/users",
  adminInstances: "/admin/instances",
  adminConnectors: "/admin/connectors",
  adminAudit: "/admin/audit",
  adminCustomers: "/admin/customers",
  adminTenants: "/admin/tenants",
  adminPlatformSettings: "/admin/platform-settings",
  // AI apps over MCP (ADR 0009, round 8): the person's own page (from the user menu) and the
  // admins' page. The consent page (`/mcp-consent`) is outside the shell and not a route here.
  connectedApps: "/connected-apps",
  adminAiApps: "/admin/ai-apps",
  // Release 8B (ADR 0009 section 6): the approve link of a write an AI app asked to make,
  // `/mcp-approve/<id>?instance=<id>`. Reached only from that link, so it has no nav entry.
  mcpApprove: MCP_APPROVE_PATH,
  // #1271: the person's own watches in this instance, the page a watcher's email links as Manage
  // notifications. Every role opens it (`instance.read`); the user menu links it, no nav entry.
  myAlerts: "/my-alerts",
} as const;

export type RouteKey = keyof typeof ROUTES;

/**
 * The Tenants screen with its New tenant dialog open (#921): where every "Create a tenant" link
 * goes, so the first run is tenant first, then instance.
 */
export const NEW_TENANT_ADDRESS = `${ROUTES.adminTenants}?new=1`;

/**
 * #1067 (owner decision, option A, 2026-10-14): who may open Admin > Users. Shown by the route
 * guard and by the page itself to anyone else; `docs/guide/settings.md` quotes it.
 */
export const USERS_DENIED_MESSAGE =
  "Users is for instance admins, tenant admins and platform admins.";

/**
 * #1106 (owner, 2026-10-14): who may open Settings (General, Destination, Notifications). Shown by
 * the route guard to anyone else; `docs/guide/settings.md` quotes it.
 */
export const SETTINGS_DENIED_MESSAGE =
  "Settings is for instance admins, tenant admins and platform admins.";

export interface AppRoute {
  path: string;
  title: string;
  description?: string;
  /**
   * The subtitle while group access is on (`GET /me` `features.groupAccess`), when it says
   * more than `description` (r5 polish): Tenants names the directory each tenant signs in from
   * only while directory groups are in use. Read through `routeDescription`.
   */
  groupAccessDescription?: string;
  /**
   * The subtitle for a reader who reaches the route through one of its platform-scope
   * permissions (round 6 walk W17): Instances lists every tenant's instances to a PlatformAdmin,
   * so "Instances of this tenant" was wrong for them. Read through `routeDescription`.
   */
  platformDescription?: string;
  /**
   * The subtitle for a reader who holds no write permission in any instance they can open
   * (`holdsMcpWritePermission`, #1393 finding 61): Connected apps never offers such a person the
   * change option, so its subtitle says read only. Read through `routeDescription`.
   */
  readOnlyDescription?: string;
  /** Section header in the nav drawer. Undefined = the leading, untitled group. */
  section?: "Admin";
  icon: IconComponent;
  anyPermission?: PermissionKey[];
  /**
   * What a reader without `anyPermission` is told instead of the generic "needs the <key>
   * permission" line, for a route no single grant opens (#1067: Users comes with a role, so asking
   * for the key is no next step). The page's own `PermissionNotice` uses the same words.
   */
  deniedMessage?: string;
  /**
   * A deployment feature the route needs (`GET /me` `features`): off, the route has no nav entry
   * for anyone, PlatformAdmin included. An address that reaches skyMonitor says it is off and who
   * turns it on (`FeatureOffNotice`). AI apps (`mcp`) do not exist at all while off
   * (`featureHiddenWhenOff`, #1812, owner 2026-10-22, reversing #1087's `navWhenOff`): no header
   * of their own, and an address reads "That page does not exist", as the server answers every
   * MCP route 404. AI apps are off while the flag is off or missing or the public address is
   * unset (`features.mcp`, the server's `mcpAvailable`).
   */
  feature?: "monitor" | "mcp";
  /**
   * Round 8: the person's own page, about them and not about an instance ("Connected apps"). It
   * has no nav entry (the user menu links it) and renders with no instance chosen.
   */
  personal?: true;
  /**
   * Release 8B: a screen reached only from a link someone was sent (the MCP approve link) or the
   * user menu (My alerts, #1271). It has no nav entry and is never a landing, but it needs an
   * instance like any other screen.
   */
  linkOnly?: true;
  /**
   * Round 3 (owner, 2026-09-30): the path of the nav entry this route is a tab of. The route gets
   * no entry of its own; that entry links to the first of its tabs the reader may open and is
   * the current one on each (`useNavSections`, `navEntryFor`). Settings is the only such page.
   */
  navUnder?: string;
  /**
   * Round 5 (owner, 2026-10-01): a screen merged into another. The path stays declared so a saved
   * link keeps its scope and its permission gate, but it has no nav entry and its page redirects
   * here. Customers folded into Tenants (round 6 T-04, platform 0027) is the only one.
   */
  mergedInto?: string;
}

/**
 * One Settings page (round 3, owner 2026-09-30: "if it's settings, remove a dedicated
 * destination and have the master name as Settings"): three routes, each titled "Settings" with
 * this one subtitle, joined by the tab strip (`components/settings/SettingsTabs.tsx`). They stay
 * three routes because each carries its own permission; the tab strip, the breadcrumb and the
 * nav entry read this list.
 */
export const SETTINGS_DESCRIPTION =
  "How this instance runs, where it loads data and who hears about its runs.";

export const SETTINGS_TABS: readonly {
  value: "settings" | "destination" | "notifications" | "connectors";
  label: string;
  path: string;
  /**
   * A tab nested under another tab's route (it inherits that guard) that needs a key of its own
   * as well: Connectors (T-51) is a tenant setting, so it asks for the tenant's key.
   */
  anyPermission?: readonly PermissionKey[];
}[] = [
  { value: "settings", label: "General", path: ROUTES.settings },
  { value: "destination", label: "Destination", path: ROUTES.settingsDestination },
  { value: "notifications", label: "Notifications", path: ROUTES.settingsNotifications },
  {
    value: "connectors",
    label: "Connectors",
    path: ROUTES.settingsConnectors,
    anyPermission: ["tenant.instances.manage"],
  },
];

export type SettingsTab = (typeof SETTINGS_TABS)[number]["value"];

/**
 * The address a Settings tab had before B-51a (round 10): Destination was `/destination` and the
 * others sat under `/admin/settings`. Links already sent (a credential email's, a bookmark, the
 * help page's) still land: the shell redirects them (`ShellRoutes`), keeping the search. Null for
 * any other address. `/destination` and `/admin/settings` match as whole segments only.
 *
 * ADR 0011 (2026-10-08): the Explorer is removed. `/explorer` and any address under it go to Your
 * data, so a bookmark lands on the page that replaced it.
 */
export function legacyAddress(pathname: string): string | null {
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const under = (base: string): string | null =>
    path === base ? "" : path.startsWith(`${base}/`) ? path.slice(base.length) : null;
  const destination = under("/destination");
  if (destination !== null) return `${ROUTES.settingsDestination}${destination}`;
  const settings = under("/admin/settings");
  if (settings !== null) return `${ROUTES.settings}${settings}`;
  if (under("/explorer") !== null) return ROUTES.yourData;
  return null;
}

/** Whether a route's feature, if it names one, is on for this deployment (`GET /me` `features`). */
export function featureAllows(
  route: Pick<AppRoute, "feature">,
  features: { monitor?: boolean; mcp?: boolean } | undefined,
): boolean {
  if (route.feature === undefined) return true;
  return features?.[route.feature] === true;
}

/**
 * #1812: a feature that, while off, does not exist for anyone: its routes read as unknown
 * addresses ("That page does not exist") rather than a notice that it is off. AI apps only;
 * skyMonitor keeps its notice.
 */
export function featureHiddenWhenOff(feature: NonNullable<AppRoute["feature"]>): feature is "mcp" {
  return feature === "mcp";
}

/**
 * A declared route as the shell may use it: undefined when its feature hides it while off
 * (`featureHiddenWhenOff`), so an AI apps address has no header, subtitle or crumb of its own.
 */
export function visibleRoute(
  route: AppRoute | undefined,
  features: { monitor?: boolean; mcp?: boolean } | undefined,
): AppRoute | undefined {
  if (!route?.feature) return route;
  if (featureAllows(route, features) || !featureHiddenWhenOff(route.feature)) return route;
  return undefined;
}

/**
 * The nav follows the workflow (owner 2026-10-09): Overview, Connections, Transformations,
 * Schedules, Runs, skyMonitor, Your data, Settings, then the Admin section. This array's order is
 * the nav's order; nothing else orders it.
 */
export const APP_ROUTES: readonly AppRoute[] = [
  // First, so it is where every instance role lands (Session 10, B1): `firstAllowedPath` takes
  // the first route the user can reach, and every instance role holds `instance.read`.
  {
    path: ROUTES.overview,
    title: "Overview",
    description:
      "How this instance's connections, schedules, imports and tests are doing right now.",
    icon: OverviewIcon,
    anyPermission: ["instance.read"],
  },
  {
    path: ROUTES.connections,
    title: "Connections",
    description: "Source systems this instance imports from, and the entities selected in each.",
    icon: ConnectionsIcon,
    anyPermission: ["dataflows.read"],
  },
  {
    path: ROUTES.transformations,
    title: "Transformations",
    description: "dbt models that run after data lands in the destination.",
    icon: TransformationsIcon,
    anyPermission: ["dbt_jobs.read"],
  },
  {
    path: ROUTES.schedules,
    title: "Schedules",
    description: "When each connection runs, and what runs next.",
    icon: SchedulesIcon,
    anyPermission: ["schedules.read"],
  },
  {
    path: ROUTES.runs,
    title: "Runs",
    description:
      "Every import and every transformation run, its steps, and the tables each one moved.",
    icon: RunsIcon,
    anyPermission: ["runs.read"],
  },
  {
    // skyMonitor (spec section 11): behind `features.flags.monitor`; every role reads it.
    path: ROUTES.monitor,
    title: "skyMonitor · Data health",
    description:
      "Data health: tables that are late, empty or unusual, and schedules that did not run.",
    icon: SkyMonitorIcon,
    anyPermission: ["monitor.read"],
    feature: "monitor",
  },
  {
    // ADR 0011 (2026-10-08, owner decision D1): where skyData puts the data and how to open it in
    // Snowflake. It shows no table and no row; every instance role reads it, a Viewer included.
    path: ROUTES.yourData,
    title: "Your data",
    description: "Where skyData puts your data, and how to open it in Snowflake.",
    icon: YourDataIcon,
    anyPermission: ["instance.read"],
  },
  {
    // Round 3: the Settings page's nav entry, where the Destination entry was; General and
    // Notifications are its other tabs (`navUnder`). #1106: Instance admin and above, so a Viewer
    // and a Contributor have no Settings item.
    // Must stay before the `/settings` entry (General) below: `App.tsx` mounts each route with
    // `nest` in this order, and `/settings` would otherwise swallow `/settings/destination`.
    path: ROUTES.settingsDestination,
    title: "Settings",
    description: SETTINGS_DESCRIPTION,
    icon: SettingsIcon,
    anyPermission: ["instance.binding.read"],
    deniedMessage: SETTINGS_DENIED_MESSAGE,
  },
  {
    path: ROUTES.adminUsers,
    title: "Users",
    description: "Who can reach this instance, and what each of them may do.",
    section: "Admin",
    icon: UsersIcon,
    anyPermission: ["instance.users.read"],
    deniedMessage: USERS_DENIED_MESSAGE,
  },
  {
    path: ROUTES.adminInstances,
    title: "Instances",
    description: "Instances of this tenant and the state of their provisioning.",
    platformDescription: "Instances of every tenant and the state of their provisioning.",
    section: "Admin",
    icon: InstancesIcon,
    anyPermission: ["tenant.instances.manage", "platform.instances.manage"],
  },
  {
    path: ROUTES.adminConnectors,
    title: "Connectors",
    description: "Connector definitions available to this deployment.",
    section: "Admin",
    icon: ConnectorsIcon,
    anyPermission: ["platform.connectors.manage"],
  },
  {
    // The General tab of Settings (and, nested, Notifications): no nav entry of its own.
    // After the Destination entry above (see its comment): a nest route matches by prefix.
    path: ROUTES.settings,
    title: "Settings",
    description: SETTINGS_DESCRIPTION,
    icon: SettingsIcon,
    anyPermission: ["instance.settings.read"],
    deniedMessage: SETTINGS_DENIED_MESSAGE,
    navUnder: ROUTES.settingsDestination,
  },
  {
    path: ROUTES.adminAudit,
    title: "Audit trail",
    description: "Who changed what and when, with the reference id for each action.",
    section: "Admin",
    icon: AuditIcon,
    anyPermission: ["instance.audit.read", "tenant.audit.read", "platform.audit.read"],
  },
  // The platform-scope screens close the Admin section (FR-20, FR-72). They are
  // NOT a second nav section: a PlatformAdmin simply sees eight items where an
  // InstanceAdmin sees three, and a second header would cost a shell change to
  // tell a reader something the items already say.
  {
    path: ROUTES.adminTenants,
    title: "Tenants",
    // Round 6 A3 and T-04 (owner 2026-10-01 23:25: a Display name and a Name; 2026-10-02:
    // "Rename. No need to use Customer").
    description: "Tenants this deployment serves: each one's names, website and instances.",
    groupAccessDescription:
      "Tenants this deployment serves: each one's names, website and instances, and the directory each signs in from.",
    section: "Admin",
    icon: TenantsIcon,
    anyPermission: ["platform.tenants.manage"],
  },
  {
    // Round 6 T-04: Customers folded into Tenants (platform 0027); this route only redirects
    // (`pages/admin/customers.tsx`), keeping `?tenant=<id>`.
    path: ROUTES.adminCustomers,
    // The title names the page it moved to (a bookmark's breadcrumb), never a nav entry.
    title: "Tenants",
    description: "Moved to Tenants. This address redirects there.",
    section: "Admin",
    icon: TenantsIcon,
    anyPermission: ["platform.tenants.manage"],
    mergedInto: ROUTES.adminTenants,
  },
  {
    path: ROUTES.adminPlatformSettings,
    title: "Platform settings",
    description:
      "Deployment-wide settings: feature flags, retention, notifications and the computed egress allow-list.",
    section: "Admin",
    icon: PlatformSettingsIcon,
    anyPermission: ["platform.settings.manage"],
  },
  {
    // ADR 0009 (round 8): who connected which AI app, revocation, and the tenant and instance
    // switches. Each section asks for its own key; the route opens for any of them.
    path: ROUTES.adminAiApps,
    title: "AI apps",
    description:
      "AI apps people have connected to read skyData, and whether AI apps may connect at all.",
    section: "Admin",
    icon: AiAppsIcon,
    anyPermission: [
      "instance.users.manage",
      "instance.settings.write",
      "tenant.users.manage",
      "tenant.instances.manage",
      "platform.settings.manage",
    ],
    feature: "mcp",
  },
  {
    path: ROUTES.connectedApps,
    title: "Connected apps",
    description:
      "AI apps you have allowed to read, or change, skyData as you. Disconnecting one ends its access at once.",
    readOnlyDescription:
      "AI apps you have allowed to read skyData as you. Disconnecting one ends its access at once.",
    icon: ConnectedAppsIcon,
    feature: "mcp",
    personal: true,
  },
  {
    path: ROUTES.mcpApprove,
    title: "Approve a change",
    description:
      "A change an AI app asked to make as you. It runs only if you approve it here, in skyData.",
    icon: ApproveChangeIcon,
    // The server answers 404 unless the change is this person's, in this instance.
    anyPermission: ["instance.read"],
    feature: "mcp",
    linkOnly: true,
  },
  {
    // #1271: a watcher's Manage notifications link, and the user menu's "Open My alerts". A Watch is
    // personal but belongs to an instance, so the page is in the instance, for every role.
    path: ROUTES.myAlerts,
    title: "My alerts",
    description: "The connections and schedules you watch in this instance. Stop any of them here.",
    icon: WatchIcon,
    anyPermission: ["instance.read"],
    linkOnly: true,
  },
] as const;

/** The first route the user can actually reach, for the post-sign-in landing. */
export function firstAllowedPath(can: (permission: PermissionKey) => boolean): string {
  // A personal page (round 8) is never a landing: it is about the person, not the work. Nor is
  // a page reached only from a link (release 8B, the approve link).
  const route = APP_ROUTES.find(
    (r) => !r.personal && !r.linkOnly && (!r.anyPermission || r.anyPermission.some(can)),
  );
  return route?.path ?? ROUTES.connections;
}

/**
 * The sections whose sub-addresses name a record of the instance, and the list each falls back to
 * after a switch (#932, owner 2026-10-13). `/runs/<id>`, `/connections/new`, `/connections/<id>`
 * (and its `/setup`), `/schedules/<id>`, `/transformations/<id>` (a dbt job, and its run's nodes)
 * name something that belongs to the instance just left, so the switch lands on the section's
 * list. An approve link (`/mcp-approve/<id>`) has no list of its own and lands on Overview.
 * Another section's sub-address is a tab or a page of its own (`/settings/notifications`) and
 * means the same thing in any instance, so it is kept.
 */
const RECORD_LISTS: ReadonlyMap<string, string> = new Map([
  [ROUTES.connections, ROUTES.connections],
  [ROUTES.schedules, ROUTES.schedules],
  [ROUTES.runs, ROUTES.runs],
  [ROUTES.transformations, ROUTES.transformations],
  [ROUTES.mcpApprove, ROUTES.overview],
]);

/**
 * The list a record's address falls back to in another instance (#932), or null when the
 * address names no record (a list, a screen with no id, a tab) and so means the same anywhere.
 */
export function listPathForRecord(location: string): string | null {
  const route = routeFor(location);
  if (!route) return null;
  const list = RECORD_LISTS.get(route.path);
  if (list === undefined) return null;
  // The list itself (`/runs`, with or without a search) is not a record.
  return pathnameOf(location) === route.path ? null : list;
}

/**
 * Where the user lands after switching instance in place (the user menu), from the location they
 * were on and what the NEW instance lets them do. A record's location is trimmed to its section's
 * list (`listPathForRecord`: `/runs/<id>` becomes `/runs`), because the record it names belongs to
 * the instance just left; a section the new instance does not allow goes to its first allowed
 * screen. Every other screen stays on the same path (owner 2026-10-13: "keep the same screen"),
 * and so does a screen that renders with no instance (platform administration, personal pages)
 * and an address no route declares.
 */
export function landingAfterInstanceSwitch(
  location: string,
  can: (permission: PermissionKey) => boolean,
): string {
  const route = routeFor(location);
  if (!route) return location;
  if (routeNeedsNoInstance(route, can)) return location;
  if (route.anyPermission && !route.anyPermission.some(can)) return firstAllowedPath(can);
  if (RECORD_LISTS.has(route.path)) return listPathForRecord(location) ?? route.path;
  return location;
}

/**
 * Whether a location is one of the two entry points the admin landing applies to (Session 10,
 * B1): `/` and `/overview`. Every other location keeps the picker redirect, so a link to
 * `/connections` with no instance chosen still asks which instance it means.
 */
export function isOverviewLanding(location: string): boolean {
  // One trailing slash is the same route to the router (`/overview/` renders Overview), so it
  // is the same landing (P2 review nit); `/overview/x` is not.
  const raw = pathnameOf(location);
  const pathname = raw.length > 1 && raw.endsWith("/") ? raw.slice(0, -1) : raw;
  return pathname === "/" || pathname === ROUTES.overview;
}

/**
 * The instance a user with none selected lands in on `/` or `/overview` (Session 10, B1): the
 * first one they can read, from the same list the picker shows (`GET /api/v2/me`), ordered by
 * tenant name and then instance name so the choice does not depend on the order the server
 * happened to return. Null when there is none; Overview then shows its empty state.
 * Only an active instance counts (#419): one still provisioning is never landed on.
 *
 * Read from the instance's own `permissions`, never from its role name (FR-13).
 */
export function landingInstance(instances: readonly MeInstance[]): MeInstance | null {
  // #419: only an active instance. One still provisioning reads "Could not load" on every
  // Overview card, so a new admin is never sent there; the picker lists it with a badge.
  const readable = instances.filter(
    (instance) => instance.status === "active" && instance.permissions.includes("instance.read"),
  );
  const sorted = [...readable].sort(
    (a, b) =>
      a.tenantDisplayName.localeCompare(b.tenantDisplayName) || a.name.localeCompare(b.name),
  );
  return sorted[0] ?? null;
}

/**
 * The platform-scope permissions a route accepts (FR-20, FR-72).
 *
 * Read from the permission catalog's own `group`, never from a list of paths kept beside this
 * one: a screen's scope is a property of the permission it needs, and a second list is how
 * `/admin/tenants` ends up gated at platform scope and treated as instance-scoped by the shell.
 */
function platformPermissionsOf(route: AppRoute): PermissionKey[] {
  return (route.anyPermission ?? []).filter((key) => PERMISSIONS[key].group === "platform");
}

/**
 * Whether a route can render with NO instance selected.
 *
 * The platform administration screens act on the deployment — tenants, instances,
 * connector definitions, platform settings, the platform audit trail — and their routes carry
 * no `x-instance-id`. Everything else reads rows that belong to one instance and keeps
 * redirecting to the picker.
 *
 * Holding the permission is part of the question, not a separate one: `/admin/instances` also
 * accepts `tenant.instances.manage`, and a TenantAdmin reaching it that way is inside an
 * instance's tenant, so the picker still comes first for them.
 */
export function routeNeedsNoInstance(
  route: AppRoute,
  can: (permission: PermissionKey) => boolean,
): boolean {
  return route.personal === true || platformPermissionsOf(route).some(can);
}

/**
 * The first platform administration screen the user can reach, or null when they hold no
 * platform-scope permission at all.
 *
 * This is what the instance picker's "Platform administration" action opens, and what makes a
 * fresh deployment escapable: with zero instances the picker is otherwise a dead end for the
 * one person who can create the first one (FR-20).
 */
export function firstPlatformPath(can: (permission: PermissionKey) => boolean): string | null {
  const route = APP_ROUTES.find((r) => platformPermissionsOf(r).some(can));
  return route?.path ?? null;
}

/** The addresses outside every instance that are not screens of the route table. */
const GLOBAL_ADDRESSES: ReadonlySet<string> = new Set([
  "/instances",
  "/callback",
  "/sign-in",
  MCP_CONSENT_PATH,
]);

/**
 * Whether an address lives outside every instance (round 9 T-58): the picker, the sign-in
 * routes, the MCP consent page, a personal screen, and a screen whose every permission is a
 * platform one (Tenants, Connectors, Platform settings). Everything else is under
 * `/i/<instance id>/` (`lib/instancePath.ts`). A screen that also takes an instance or tenant key
 * (Instances, Audit trail, AI apps) is inside the instance: what it shows there depends on it.
 * Read from the permission catalog's own `group`, never from a list of paths.
 */
export function isGlobalPath(location: string): boolean {
  const pathname = pathnameOf(location);
  if (GLOBAL_ADDRESSES.has(pathname)) return true;
  const route = routeFor(pathname);
  if (!route) return false;
  if (route.personal) return true;
  const keys = route.anyPermission ?? [];
  return keys.length > 0 && keys.every((key) => PERMISSIONS[key].group === "platform");
}

/** Whether the location the user is at can render without an instance. */
export function locationNeedsNoInstance(
  location: string,
  can: (permission: PermissionKey) => boolean,
): boolean {
  const route = routeFor(location);
  return route !== undefined && routeNeedsNoInstance(route, can);
}

function pathnameOf(location: string): string {
  const cut = location.indexOf("?");
  return cut === -1 ? location : location.slice(0, cut);
}

/**
 * The declared route for a location, matching the longest declared prefix so a
 * detail route (`/runs/<id>`) inherits its list's header rather than deriving
 * one. Undefined when nothing is declared — the caller then derives a title
 * from the slug and shows NO description, so a gap reads as provisional rather
 * than as a different page.
 */
export function routeFor(location: string): AppRoute | undefined {
  const pathname = pathnameOf(location);
  const exact = APP_ROUTES.find((r) => r.path === pathname);
  if (exact) return exact;
  return APP_ROUTES.filter((r) => pathname.startsWith(`${r.path}/`)).sort(
    (a, b) => b.path.length - a.path.length,
  )[0];
}

/**
 * The subtitle a route shows (r5 polish): `groupAccessDescription` while group access is on and
 * the route has one, else `description`. A clause about directories reads as a broken promise
 * on a deployment that maps none.
 */
export function routeDescription(
  route: AppRoute | undefined,
  features: { groupAccess: boolean; platformScope?: boolean; mcpWriter?: boolean },
): string | undefined {
  if (!route) return undefined;
  if (features.platformScope && route.platformDescription) return route.platformDescription;
  // Only a known read-only reader: while `/me` is not read yet the full subtitle stands.
  if (features.mcpWriter === false && route.readOnlyDescription) return route.readOnlyDescription;
  return (features.groupAccess ? route.groupAccessDescription : undefined) ?? route.description;
}

/**
 * Whether the reader reaches this route through one of its platform-scope permissions (a
 * PlatformAdmin on Instances), from the permission catalog's own `group`, never a role name.
 */
export function readsAtPlatformScope(
  route: AppRoute | undefined,
  can: (permission: PermissionKey) => boolean,
): boolean {
  return route ? platformPermissionsOf(route).some(can) : false;
}

/**
 * The nav entry that is current at a location (round 3): the entry a Settings tab belongs to
 * (`navUnder`), else the location itself, as before (a nav item's id is its route's path).
 */
export function navSelectionFor(location: string): string {
  // The shell's router already takes `/i/<instance>` off; a full address is read the same way.
  const pathname = pathnameOf(splitInstancePath(location).inner);
  const route = routeFor(pathname);
  // #989: a detail page (`/runs/<id>`) keeps its list's entry selected, so the route's own path.
  return route?.navUnder ?? route?.path ?? pathname;
}

/** "/admin/audit" → "Admin Audit". Never another page's title. */
export function derivePageTitle(location: string): string {
  const words = pathnameOf(location)
    .split("/")
    .filter(Boolean)
    .flatMap((segment) => segment.split("-"))
    .filter(Boolean);
  if (words.length === 0) return "skyData";
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/**
 * The connection editor lives under the Connections route rather than beside it
 * (`/connections/new`, `/connections/<id>`), so `routeFor` gives it the
 * Connections header and the nav rail keeps Connections selected while it is
 * open. It is deliberately NOT in `APP_ROUTES`: that table is what the nav
 * drawer renders, and a per-connection destination has no place in a nav list.
 */
export const NEW_CONNECTION_PATH = `${ROUTES.connections}/new`;

export function connectionPath(id: string): string {
  return `${ROUTES.connections}/${id}`;
}

/**
 * An absolute destination for a `Link` or a `navigate` call made from INSIDE a
 * nested router.
 *
 * `App.tsx` mounts every section with wouter's `nest`, so a `useLocation`
 * navigate inside one resolves against that section's base:
 * `navigate("/connections/new")` from within `/connections` lands on
 * `/connections/connections/new`, which matches nothing and renders the
 * not-found page. `~` is wouter's own escape for "this path is already
 * absolute" and it works for both `Link href` and `navigate`.
 *
 * Every cross-section link — Runs to a connection, Schedules to a connection —
 * needs it, so it lives here beside the paths rather than as a `~` typed by
 * hand at each call site.
 */
export function absolutePath(path: string): string {
  return `~${path}`;
}

/** One step of the shell's breadcrumb: a link back when `href` is set, plain text otherwise. */
export interface Crumb {
  label: string;
  href?: string;
}

/**
 * Detail screens that name their record: a run by its id (in the path), a schedule and a
 * transformation by the name the page publishes (`PageCrumb`, post-release 607e63a). Until the
 * page has named it, the last step says what kind of record it is. First match wins.
 */
const RECORD_LEAVES: ReadonlyArray<readonly [RegExp, (pathname: string) => string]> = [
  // No decode: wouter's `useLocation` has already decoded the path (and its decode catches a
  // malformed escape). A second decode threw URIError on "/runs/100%" and unmounted the shell.
  [/^\/runs\/[^/]+$/, (pathname) => pathname.slice("/runs/".length)],
  [/^\/schedules\/new$/, () => "New schedule"],
  [/^\/schedules\/[^/]+$/, () => "Schedule"],
  [/^\/transformations\/[^/]+$/, () => "dbt job"],
];

/** The last step of a nested screen that has no breadcrumb of its own. */
const NESTED_LEAVES: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/connections\/new$/, "New connection"],
  // Release 8B (ADR 0009 section 7): a draft an AI app created, finished in skyData.
  [/^\/connections\/[^/]+\/setup$/, "Finish setup"],
  [/^\/connections\/[^/]+$/, "Connection"],
];

/**
 * The breadcrumb the shell draws above the page header on EVERY screen (SKILL "Breadcrumbs are
 * mandatory"; client/AGENTS.md "Page layout"), read from the same route table as the nav and
 * the title. The section ("Admin") is text, not a link: it has no page of its own. The last step
 * is the current page. On a record's detail screen (`RECORD_LEAVES`) it is the record's name,
 * `recordName` when the page has published one (`PageCrumb`); elsewhere `recordName` is ignored.
 * A Settings tab (round 3) reads "Settings", as text like a section, then the tab: the tabs are
 * siblings, and the strip is the way between them.
 */
export function breadcrumbTrail(
  location: string,
  recordName: string | null = null,
  /** `GET /me` `features`: an AI apps address while they are off is an unknown one (#1812). */
  features?: { monitor?: boolean; mcp?: boolean },
): Crumb[] {
  const raw = pathnameOf(location);
  const pathname = raw.length > 1 && raw.endsWith("/") ? raw.slice(0, -1) : raw;

  const tab = SETTINGS_TABS.find((candidate) => candidate.path === pathname);
  if (tab) return [{ label: "Settings" }, { label: tab.label }];

  const declared = routeFor(pathname);
  const route = features === undefined ? declared : visibleRoute(declared, features);
  if (!route) return [{ label: derivePageTitle(pathname) }];

  const section: Crumb[] = route.section ? [{ label: route.section }] : [];
  const record = RECORD_LEAVES.find(([pattern]) => pattern.test(pathname))?.[1];
  const leaf = record
    ? (recordName ?? record(pathname))
    : NESTED_LEAVES.find(([pattern]) => pattern.test(pathname))?.[1];
  if (pathname === route.path || !leaf) return [...section, { label: route.title }];
  return [...section, { label: route.title, href: route.path }, { label: leaf }];
}
